import * as React from 'react'
import { observer } from 'mobx-react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetTitle,
} from 'components/ModalSheet'
import Button from 'components/Button'
import MessageBankDialogViewModel from './MessageBankDialogViewModel'
import ContentLoading from 'components/ContentLoading'
import styles from './MessageBankDialog.sass'
import {
  SplitView,
  SplitViewMain,
  SplitViewSide,
  SplitViewSidebarListItem,
  SplitViewSidebarListItemArrow,
  SplitViewSidebarListItemTitle,
} from 'components/SplitView'
import { Sandwich, SandwichContent } from 'components/Sandwich'
import ScrollView from 'components/ScrollView'
import { EmptyState, EmptyStateIllustrationImage } from 'components/EmptyState'
import { useBrandingSettings } from 'hooks/branding'
import { MessageBankReply } from '../MessageBankReply'

interface MessageBankDialogProps {
  vm: MessageBankDialogViewModel
}
const MESSAGE_BANK_MAX_HEIGHT = 480

export default observer(function MessageBankDialog({
  vm,
}: MessageBankDialogProps) {
  const isLoadingInitial = vm.tasks.pending

  const { corporateMode } = useBrandingSettings()

  return (
    <ModalSheet showing={vm.showing} onRequestClose={vm.close} large>
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>Message Bank</ModalSheetTitle>
      </ModalSheetHeader>
      <ModalSheetContent>
        {isLoadingInitial ? (
          <ContentLoading compact />
        ) : (
          <>
            {vm.messageBankHasReplies ? (
              <SplitView>
                <div className={styles.splitViewSideConstraint}>
                  <SplitViewSide>
                    <MessageBankList>
                      {vm.messageBankReplies.map((reply) => (
                        <MessageBankListItem
                          active={vm.selectedReply === reply}
                          onSelect={() => vm.setSelectedReply(reply)}
                          key={reply.id}
                          reply={reply}
                        />
                      ))}
                    </MessageBankList>
                  </SplitViewSide>
                </div>
                <SplitViewMain>
                  <MessageBankItemDetail
                    key={vm.selectedReply.id}
                    reply={vm.selectedReply}
                  />
                </SplitViewMain>
              </SplitView>
            ) : (
              <div className={styles.emptyStateContainer}>
                <EmptyState
                  className={styles.emptyState}
                  hideIllustration={corporateMode}
                  image={
                    <EmptyStateIllustrationImage
                      src={require('assets/images/img_nojobstodisplay.svg')}
                    />
                  }
                  title="No replies yet"
                  subtitle="Your workspace administrator hasn't configured any replies"
                  animate
                />
              </div>
            )}
          </>
        )}
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button onClick={vm.close} type="default">
            Close
          </Button>
          {vm.messageBankHasReplies && (
            <Button
              onClick={vm.useSelectedReply}
              loading={vm.tasks.pending}
              type="primary"
              title={'Insert this reply into the message composer'}
              disabled={vm.tasks.pending}
            >
              Select Reply
            </Button>
          )}
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

interface MessageBankListProps {
  children: React.ReactNode
}

const MessageBankList = observer(function MessageBankList({
  children,
}: MessageBankListProps) {
  return (
    <Sandwich stretch>
      <SandwichContent>
        <ScrollView maxHeight={MESSAGE_BANK_MAX_HEIGHT}>{children}</ScrollView>
      </SandwichContent>
    </Sandwich>
  )
})

interface MessageBankListItemProps {
  reply: MessageBankReply
  onSelect: () => void
  active?: boolean
}

const MessageBankListItem = observer(function MessageBankListItem({
  reply,
  onSelect,
  active,
}: MessageBankListItemProps) {
  return (
    <SplitViewSidebarListItem
      spacing="compact"
      onClick={onSelect}
      active={active}
      extras={<SplitViewSidebarListItemArrow />}
    >
      <SplitViewSidebarListItemTitle wrap>
        {reply.title}
      </SplitViewSidebarListItemTitle>
    </SplitViewSidebarListItem>
  )
})

interface MessageBankItemDetailProps {
  reply: MessageBankReply
}
const MessageBankItemDetail = observer(function MessageBankItemDetail({
  reply,
}: MessageBankItemDetailProps) {
  return (
    <ScrollView maxHeight={MESSAGE_BANK_MAX_HEIGHT}>
      <div className={styles.replyDetail}>{reply.content}</div>
    </ScrollView>
  )
})
